import { ParticleLayer, TileLayer, ArrowLayer, IsolineLayer } from '@wavetrak/maptiler';

export enum ChartPageType {
  HOMEPAGE = 'HOMEPAGE',
  SPOT_CHART = 'SPOT_CHART',
  EMBED = 'EMBED',
}

export interface ChartSpot {
  id: string;
  name?: string;
  lat?: number;
  lon?: number;
}

export interface ChartsPageQueryParams {
  chartType: ChartSlug;
  startDate?: string;
  native?: boolean;
  spotId?: string;
  days?: string;
  client_id?: string;
  pinnedLocation?: string;
  embedType?: string;
}

export interface ChartPageProps {
  chartPageType: ChartPageType;
  chartPageQuery: ChartsPageQueryParams;
}

export type ChartSlug =
  | 'wave-height'
  | 'wind'
  | 'period'
  | 'water-temp'
  | 'energy'
  | 'precipitation';

export enum DataLayers {
  WAVE = 'WAVE',
  PERIOD = 'PERIOD',
  WIND = 'WIND',
  SST = 'SST',
  ENERGY = 'ENERGY',
  PRECIPITATION = 'PRECIPITATION',
}

export enum BaseLayers {
  WAVE_ARROWS = 'WAVE_ARROWS',
  PRESSURE = 'PRESSURE',
  JET_STREAM = 'JET_STREAM',
  SEA_ICE = 'SEA_ICE',
}

export enum VersionedLayers {
  WAVE_V2 = 'WAVE_V2',
  WAVE_V3 = 'WAVE_V3',
  ENERGY_V1 = 'ENERGY_V1',
  PERIOD = 'PERIOD',
  PERIOD_V2 = 'PERIOD_V2',
  WIND = 'WIND',
  SST = 'SST',
  PRESSURE = 'PRESSURE',
  JET_STREAM = 'JET_STREAM',
  SEA_ICE = 'SEA_ICE',
}

export const ChartLayers = {
  ...DataLayers,
  ...BaseLayers,
};

export type ChartLayerType = DataLayers | BaseLayers;

export type Layer = TileLayer | ParticleLayer | ArrowLayer | IsolineLayer;

type UnixSeconds = number;

export interface Tile {
  id: string;
  timestamp: UnixSeconds;
}

export interface DataLayerProps {
  currentTime: number;
  setCurrentTime: (time: number) => void;
  setShowPaywall: (show: boolean) => void;
  setAnimationEnd: (end: boolean) => void;
  animationEnd: boolean;
  endTime: number;
  setPlaying: (playing: boolean) => void;
  playing: boolean;
  minutesPerSecond: number;
  tiles: Tiles;
  isEmbed: boolean;
  insertBeforeID?: string;
  embedType?: string;
}

export type Tiles = Tile[];

export const segmentLayerTypes = {
  [DataLayers.WAVE]: 'wave height',
  [DataLayers.PERIOD]: 'wave period',
  [DataLayers.ENERGY]: 'wave energy',
  [DataLayers.WIND]: 'wind',
  [DataLayers.SST]: 'sea temp',
  [DataLayers.PRECIPITATION]: 'precipitation',
};

export const segmentLayerSlugs = {
  [DataLayers.WAVE]: 'wave-height',
  [DataLayers.PERIOD]: 'period',
  [DataLayers.ENERGY]: 'energy',
  [DataLayers.WIND]: 'wind',
  [DataLayers.SST]: 'water-temp',
  [DataLayers.PRECIPITATION]: 'precipitation',
};

export const LayerToTitleMapping: Record<ChartSlug, string> = {
  'wave-height': `Wave Height Forecast Chart - Surfline`,
  wind: `Wind Forecast Chart - Surfline`,
  'water-temp': `Sea Surface Temperature Forecast Chart - Surfline`,
  period: `Wave Period Forecast Chart - Surfline`,
  energy: `Wave Energy Forecast Chart - Surfline`,
  precipitation: `Precipitation Forecast Chart - Surfline`,
};
